import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";
import CategorySelection from "./CategorySelection";
import StatelyForm from "./StatelyForm";
import handleDelayScroll from "@utils/handleDelayScroll";
import PracticePatientSelector from "./PracticePatientSelector";
import Recommendation from "./Recommendation";
import useGetStateMachine from "@hooks/useGetStateMachine";
import {
  AntibioticContext,
  PatientType,
  PracticeType,
} from "@context/antibioticContext";
import { OnboardingContext } from "@context/onboardingContext";
import Analytics from "@utils/analytics";
import PreferenceToast from "./PreferenceToast";

type Step = "Category" | "WorkFlow" | "Recommendation";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 100px;
  padding-bottom: 40px;
`;

const AntibioticsForm = () => {
  const [recommendationId, setRecommendationId] = useState("");
  const [flow, setFlow] = useState("");
  const [steps, setSteps] = useState<Step[]>([]);
  const categoryRef = useRef<HTMLDivElement>(null);
  const workFlowRef = useRef<HTMLDivElement>(null);
  const recommendationRef = useRef<HTMLDivElement>(null);

  const { handleChangeType, type } = useContext(AntibioticContext);
  const { userData } = useContext(OnboardingContext) || {};

  const { practice, patient } = type;

  const [statelyEvents, setStatelyEvents] = useState<string[]>([]);
  const [stateMachine, setStateMachine] = useState(null);
  const { stateMachine: machine, errorMessage } = useGetStateMachine(flow);

  const [showPreferenceToast, setShowPreferenceToast] = useState(false);
  const [autoSkippedToCategory, setAutoSkippedToCategory] = useState(false);
  const [selectorResetKey, setSelectorResetKey] = useState(0);

  useEffect(() => {
    if (machine) setStateMachine(machine);
  }, [machine]);

  // FIXED: Check for saved preferences on mount and auto-skip
  useEffect(() => {
    const rememberPrefs = localStorage.getItem('whata_remember_prefs') === 'true';

    // Read from practice_patient (single object) not separate keys
    const savedPrefsJson = localStorage.getItem('practice_patient');
    let savedPrefs = null;
    if (savedPrefsJson) {
      try {
        savedPrefs = JSON.parse(savedPrefsJson);
      } catch {
        localStorage.removeItem('practice_patient');
      }
    }

    if (rememberPrefs && savedPrefs?.practice && savedPrefs?.patient) {
      // Auto-apply preferences
      handleChangeType?.(savedPrefs.practice, savedPrefs.patient);
      handleAddStep("Category");
      setAutoSkippedToCategory(true);
      setShowPreferenceToast(true);

      // Log analytics for auto-skip
      Analytics.trackAntibioticsFormSubmission({
        practice_type: savedPrefs.practice,
        event_category: 'preference_auto_skip',
        patient_type: savedPrefs.patient
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // FIXED: Handle undo from toast notification
  const handleUndoPreferences = useCallback(() => {
    // Clear saved preferences
    localStorage.removeItem('whata_remember_prefs');
    // Note: Keep practice_patient for usePersistState compatibility
    
    // Reset to selector
    setShowPreferenceToast(false);
    setAutoSkippedToCategory(false);
    clearAllStep();
    
    // Reset to default selections
    handleChangeType?.("Secondary", "Adult");
  }, [handleChangeType]);

  const handleChangePreferences = useCallback(() => {
    setAutoSkippedToCategory(false);
    clearAllStep();
    setSelectorResetKey(k => k + 1);
  }, []);

  const handleAddEvents = useCallback(
    (events: string[]) => {
      setStatelyEvents((prev) => [practice, patient, ...events]);
    },
    [patient, practice]
  );

  const handleAddStep = useCallback(
    (step: Step) => {
      setSteps((prevSteps) => {
        const stepsSet = new Set(prevSteps);
        stepsSet.add(step);
        return Array.from(stepsSet);
      });
    },
    [setSteps]
  );

  const clearAllStep = useCallback(() => {
    setSteps([]);
  }, [setSteps]);

  const handleResetCategory = useCallback(
    (selectedPractice: PracticeType, selectedPatient: PatientType) => {
      if (practice !== selectedPractice || patient !== selectedPatient) {
        clearAllStep();
      }
      handleAddStep("Category");
    },
    [practice, patient, clearAllStep, handleAddStep]
  );

  const onFormFinished = useCallback(
    (context: { link: string }, events?: string[]) => {
      events && handleAddEvents(events);
      handleAddStep("Recommendation");
      setRecommendationId(context.link);
      Analytics.trackAntibioticsFormSubmission({
        practice_type: practice,
        event_category: 'form_interaction',
        patient_type: patient
      });
    },
    [handleAddStep, handleAddEvents, practice, patient]
  );

  // FIXED: Handle new start
  const handleNewStart = useCallback(() => {
    // CRITICAL: Clear all workflow-specific state for safety
    clearAllStep();
    setRecommendationId("");
    setFlow("");
    setStateMachine(null);
    setStatelyEvents([]);
    
    // Check if preferences are saved
    const rememberPrefs = localStorage.getItem('whata_remember_prefs') === 'true';

    // Read from practice_patient (single object)
    const savedPrefsJson = localStorage.getItem('practice_patient');
    let savedPrefs = null;
    if (savedPrefsJson) {
      try {
        savedPrefs = JSON.parse(savedPrefsJson);
      } catch {
        localStorage.removeItem('practice_patient');
      }
    }

    if (rememberPrefs && savedPrefs?.practice && savedPrefs?.patient) {
      // Preferences exist - return to CategorySelection with saved preferences
      handleChangeType?.(savedPrefs.practice, savedPrefs.patient);
      handleAddStep("Category");
      // Scroll to category selection so it's visible
      setTimeout(() => handleDelayScroll(categoryRef), 100);
      // Keep autoSkippedToCategory true so selector stays hidden
      // Don't show toast again
    } else {
      // No preferences - show selector and scroll to top
      setAutoSkippedToCategory(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // User will select practice/patient again
    }
  }, [clearAllStep, handleChangeType, handleAddStep]);

  const handleResetStateMachine = useCallback(() => {
    setRecommendationId("");
  }, []);

  const scrollToCategory = useCallback(() => {
    if (steps.includes("Category")) {
      handleDelayScroll(categoryRef);
    }
  }, [steps]);

  const scrollToStateMachine = useCallback(() => {
    if (steps.includes("WorkFlow")) {
      handleDelayScroll(workFlowRef);
    }
  }, [steps]);

  return (
    <Container>
      {showPreferenceToast && autoSkippedToCategory && (
        <PreferenceToast
          practice={practice}
          patient={patient}
          onUndo={handleUndoPreferences}
        />
      )}

      {!autoSkippedToCategory && (
        <PracticePatientSelector
          key={selectorResetKey}
          onClick={(practice, patient) => {
            scrollToCategory();
            handleResetCategory(practice, patient);
            handleChangeType?.(practice, patient);
          }}
          practice={practice}
          patient={patient}
        />
      )}

      {steps?.includes("Category") && (
        <div ref={categoryRef}>
          <CategorySelection
            patient={patient}
            practice={practice}
            onClick={(flowName: string) => {
              if (flow !== flowName) {
                setStateMachine(null);
              }

              scrollToStateMachine();
              handleAddStep("WorkFlow");
              setFlow(flowName);
            }}
            onCategoriesAvailable={scrollToCategory}
            onChangePreferences={handleChangePreferences}
          />
        </div>
      )}

      {steps?.includes("WorkFlow") && stateMachine && (
        <div ref={workFlowRef}>
          <StatelyForm
            onFormStart={scrollToStateMachine}
            stateMachine={stateMachine}
            onFormFinished={onFormFinished}
            preFiringEvents={[
              { type: practice },
              { type: patient },
              {
                type: "setLocation",
                param: {
                  district: userData?.district || "",
                  location: userData?.location || "",
                  role: userData?.role || "",
                  practiceArea: userData?.practiceArea || "",
                },
              },
            ]}
            onReset={handleResetStateMachine}
          />
        </div>
      )}

      {steps?.includes("WorkFlow") && errorMessage && (
        <div ref={workFlowRef}>{errorMessage}</div>
      )}

      {steps?.includes("Recommendation") && recommendationId && (
        <div ref={recommendationRef}>
          <Recommendation
            id={recommendationId}
            onEdit={scrollToCategory}
            onNewStart={handleNewStart}
            onRecommendationAvailable={() =>
              handleDelayScroll(recommendationRef)
            }
            events={statelyEvents}
          />
        </div>
      )}
    </Container>
  );
};

export default AntibioticsForm;